<template>
  <v-card outlined class="mb-6 pa-6">
    <!-- Header -->
    <v-row class="mb-6" align="center" justify="space-between">
      <v-col cols="12" md="6">
        <h2 class="text-h6 font-weight-bold">{{ $t("examAnalytics.reliability") }}</h2>
      </v-col>
    </v-row>

    <!-- Student Scores Table -->
    <v-card class="mb-6" outlined>
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="font-weight-medium">{{ $t("examAnalytics.studentScores") }}</span>
        <exportJsonExcel
          :data="excelData"
          :fields="excelFields"
          :filename="$t('examAnalytics.studentScores')"
          :exportBtnProps="{ depressed: true }"
        />
      </v-card-title>
      <v-card-text class="pa-0">
        <v-simple-table dense>
          <thead>
            <tr>
              <th style="min-width: 200px;">{{ $t("studentName") }}</th>
              <th
                v-for="(n, i) in Questions"
                :key="`q-${n.questionGuid}`"
                :title="n.questionTitle"
                class="text-center"
              >
                {{ $t("examAnalytics.Q") + (i + 1) }}
              </th>
              <th class="text-center">{{ $t("profile.Total") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(student, index) in paginatedStudents" :key="`student-${index}`">
              <td>{{ student.studentName }}</td>
              <td v-for="(n, i) in Questions" :key="`score-${n.questionGuid}`" class="text-center">
                {{ formatSmartDecimal(getQuestonScore(student.scores, n.questionGuid) || 0) }}
              </td>
              <td class="text-center font-weight-medium">
                {{ formatSmartDecimal(calculateTotal(student.scores)) }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-divider></v-divider>
        <v-row align="center" justify="space-between" class="ma-2">
          <!-- Student Count -->
          <v-col cols="12" md="4" class="text-start">
            <span class="grey--text font-weight-medium"
              >{{ reliabilityData.length }} {{ $t("student") }}</span
            >
          </v-col>

          <!-- Pagination Control -->
          <v-col cols="12" md="4" class="text-center">
            <v-pagination v-model="page" :length="pageCount" :total-visible="15" color="primary" />
          </v-col>

          <!-- Items Per Page -->
          <v-col cols="12" md="4" class="text-end">
            <v-select
              v-model="itemsPerPage"
              :items="[5, 10, 15, 25]"
              :label="$t('rowsPerPage')"
              dense
              outlined
              hide-details
              style="max-width: 160px; display: inline-block"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Cronbach & Variance Grid -->
    <v-row dense>
      <v-col cols="12" md="4">
        <v-card
          class="pa-4"
          :color="$vuetify.theme.dark ? 'reliability-card-cronbachAlpha' : 'grey lighten-4'"
          outlined
        >
          <h3 class="font-weight-medium mb-2">{{ $t("examAnalytics.CronbachAlpha") }}</h3>
          <div class="text-h4 font-weight-bold text-primary mb-2">
            {{ ExamStatisticsData.cronbachAlpha }}
          </div>
          <v-chip :color="getReliabilityColor" :text-color="getReliabilityTextColor" small label>
            {{ $t("examAnalytics." + ExamStatisticsData.reliabilityLevel) }}
          </v-chip>
          <p class="mt-4 text-body-2">
            {{ $t("examAnalytics." + ExamStatisticsData.message) }}
          </p>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <Bar
          :key="reliabilityChartData.labels.length"
          :datasets="reliabilityChartData.datasets"
          :labels="reliabilityChartData.labels"
          :plugins="reliabilityChartOptions.plugins"
          :scales="reliabilityChartOptions.scales"
          class="mb-3"
        ></Bar>
      </v-col>
    </v-row>
  </v-card>
</template>
<script>
import Bar from "../../charts/Bar.vue";
import exportJsonExcel from "../../export-json-excel.vue";

export default {
  name: "Reliability",
  components: { Bar, exportJsonExcel },
  props: {
    ExamStatisticsData: {
      type: Object,
      required: true
    },
    Questions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      page: 1,
      itemsPerPage: 10,
      reliabilityData: [],
      reliabilityChartData: {
        labels: [],
        datasets: [
          {
            label: this.$t("examAnalytics.QuestionVariance"),
            backgroundColor: "#3B82F6",
            data: []
          }
        ]
      },
      reliabilityChartOptions: {
        responsive: true,
        
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => `${this.$t("examAnalytics.variance")}: ${ctx.parsed.y.toFixed(2)}`
            }
          },
          datalabels: {
            color: this.$vuetify.theme.dark ? "white" : "#000",
            anchor: "end",
            align: "top",
            anchor: "end",offset: -7,
            textAlign: "center",
            formatter: value => value.toFixed(2), // Show only 2 decimals
            font: {
              size: 12
            }
          }
        },
        scales: {
          x: {
            title: { display: true, text: "Question #" },
            ticks: { color: "#666" },
            grid: {
              color: this.$vuetify.theme.dark ? "#585858" : "#e0e0e0",
              borderDash: [1, 1]
            }
          },
          y: {
            grid: { 
              color: this.$vuetify.theme.dark ? "#585858" : "#e0e0e0",
              borderDash: [4, 4]
            },
            
          }
        }
      }
    };
  },
  created() {
    this.reliabilityData = this.ExamStatisticsData.students.map(student => ({
      studentName: student.studentName,
      scores: student.scores
    }));
  },
  mounted() {
    this.$nextTick(() => {
      this.computeReliabilityChart();
    });
  },
  computed: {
    paginatedStudents() {
      const start = (this.page - 1) * this.itemsPerPage;
      return this.reliabilityData.slice(start, start + this.itemsPerPage);
    },
    pageCount() {
      return Math.ceil(this.reliabilityData.length / this.itemsPerPage);
    },
    getReliabilityColor() {
      const level = this.ExamStatisticsData.reliabilityLevel;
      if (level === "Good Reliability") return "green lighten-4";
      if (level === "Moderate Reliability") return "orange lighten-4";
      return "red lighten-4";
    },
    getReliabilityTextColor() {
      const level = this.ExamStatisticsData.reliabilityLevel;
      if (level === "Good Reliability") return "green darken-2";
      if (level === "Moderate Reliability") return "orange darken-2";
      return "red darken-2";
    },
    excelFields() {
      const fields = {
        [this.$t("studentName")]: "studentName"
      };

      this.Questions.forEach((q, index) => {
        fields[`${this.$t("examAnalytics.Q")}${index + 1}`] = `Q${index + 1}`;
      });

      fields[this.$t("profile.Total")] = "total";

      return fields;
    },

    excelData() {
      return this.reliabilityData.map(student => {
        const row = {
          studentName: student.studentName
        };

        this.Questions.forEach((q, index) => {
          row[`Q${index + 1}`] = this.formatSmartDecimal(
            this.getQuestonScore(student.scores, q.questionGuid) || 0
          );
        });

        row.total = this.formatSmartDecimal(this.calculateTotal(student.scores));
        return row;
      });
    }
  },
  methods: {
    calculateTotal(scores) {
      return scores.map(m => m.score).reduce((a, b) => a + Number(b || 0), 0);
    },
    getQuestonScore(scores, questionGuid) {
      const found = scores.find(m => m.questionGuid == questionGuid);
      return parseFloat((found && found.score) || 0);
    },
    computeReliabilityChart() {
      const questionVariances = [];
      const labels = [];

      for (let q = 0; q < this.Questions.length; q++) {
        const scores = this.reliabilityData.map(student => {
          const found = student.scores.find(m => m.questionGuid == this.Questions[q].questionGuid);
          return parseFloat((found && found.score) || 0);
        });
        const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        const variance =
          scores.reduce((sum, score) => sum + Math.pow(score - mean, 2), 0) / scores.length;
        questionVariances.push(variance);
        labels.push(`${this.$t("examAnalytics.Q")}${q + 1}`);
      }

      this.reliabilityChartData.labels = labels;
      this.reliabilityChartData.datasets[0].data = questionVariances;
    }
  }
};
</script>
<style>
.reliability-card-cronbachAlpha {
  background-color: #181818 !important;
  border-color: #333333 !important;
}
.v-application .text-body-2 {
  font-family: unset !important;
}
</style>
